import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { competitorApi } from '@/services/api/competitorApi';
import { extractError } from '@/lib/extractError';
import { notifySetupProgress } from '@/lib/setupEvents';
import type {
  CompetitorAccount,
  CompetitorPost,
  AddCompetitorDto,
  UpdateCompetitorDto,
  CompetitorPostsParams,
  CompetitorInsights,
} from '@/types';

interface CompetitorState {
  competitors: CompetitorAccount[];
  competitorPosts: CompetitorPost[];
  insights: CompetitorInsights | null;
  /** Pagination */
  page: number;
  totalPages: number;
  total: number;
  /** Loading states */
  isLoading: boolean;
  isLoadingPosts: boolean;
  isLoadingInsights: boolean;
  isAdding: boolean;
  removingIds: Set<string>;
  /** Error state */
  error: string | null;
}

/**
 * True when the list already contains an account for the same platform and
 * username (compared case-insensitively). Lets us show a specific message
 * instead of the generic error the backend returns for a duplicate insert.
 */
export function isDuplicateCompetitor(
  competitors: CompetitorAccount[],
  platform: AddCompetitorDto['platform'],
  username: string
): boolean {
  const handle = username.trim().toLowerCase();
  return competitors.some(
    c => c.platform === platform && c.username.trim().toLowerCase() === handle
  );
}

interface CompetitorActions {
  /** @returns true on success, false if the request failed (for backoff polling). */
  fetchCompetitors: () => Promise<boolean>;
  addCompetitor: (data: AddCompetitorDto) => Promise<CompetitorAccount | null>;
  updateCompetitor: (id: string, data: UpdateCompetitorDto) => Promise<CompetitorAccount | null>;
  removeCompetitor: (id: string) => Promise<void>;
  fetchPosts: (params?: CompetitorPostsParams) => Promise<void>;
  /** @returns true on success, false if the request failed (for backoff polling). */
  fetchInsights: () => Promise<boolean>;
  clearError: () => void;
}

export const useCompetitorStore = create<CompetitorState & CompetitorActions>()(
  devtools(
    (set, get) => ({
      competitors: [],
      competitorPosts: [],
      insights: null,
      page: 1,
      totalPages: 1,
      total: 0,
      isLoading: false,
      isLoadingPosts: false,
      isLoadingInsights: false,
      isAdding: false,
      removingIds: new Set(),
      error: null,

      fetchCompetitors: async () => {
        set({ isLoading: true, error: null }, false, 'competitors/fetch');
        try {
          const competitors = await competitorApi.listCompetitors();
          set(
            { competitors: competitors ?? [], isLoading: false },
            false,
            'competitors/fetch/success'
          );
          return true;
        } catch (err) {
          set({ isLoading: false, error: extractError(err) }, false, 'competitors/fetch/error');
          return false;
        }
      },

      addCompetitor: async data => {
        if (isDuplicateCompetitor(get().competitors, data.platform, data.username)) {
          set(
            { error: 'This competitor is already in your list.' },
            false,
            'competitors/add/duplicate'
          );
          return null;
        }
        set({ isAdding: true, error: null }, false, 'competitors/add');
        try {
          const competitor = await competitorApi.addCompetitor(data);
          set(
            state => ({
              competitors: [...state.competitors, competitor],
              isAdding: false,
            }),
            false,
            'competitors/add/success'
          );
          notifySetupProgress('competitors', true);
          return competitor;
        } catch (err) {
          set({ isAdding: false, error: extractError(err) }, false, 'competitors/add/error');
          return null;
        }
      },

      updateCompetitor: async (id, data) => {
        set({ error: null }, false, 'competitors/update');
        try {
          const updated = await competitorApi.updateCompetitor(id, data);
          set(
            state => ({
              competitors: state.competitors.map(c => (c._id === id ? updated : c)),
            }),
            false,
            'competitors/update/success'
          );
          return updated;
        } catch (err) {
          set({ error: extractError(err) }, false, 'competitors/update/error');
          return null;
        }
      },

      removeCompetitor: async id => {
        set(
          state => ({
            removingIds: new Set([...state.removingIds, id]),
            error: null,
          }),
          false,
          'competitors/remove'
        );
        try {
          await competitorApi.removeCompetitor(id);
          set(
            state => {
              const next = new Set(state.removingIds);
              next.delete(id);
              return {
                competitors: state.competitors.filter(c => c._id !== id),
                competitorPosts: state.competitorPosts.filter(p => p.competitorAccountId !== id),
                removingIds: next,
              };
            },
            false,
            'competitors/remove/success'
          );
          // Never optimistic: a removal can take the count back to zero, so the
          // step's state has to come from the backend.
          notifySetupProgress('competitors');
        } catch (err) {
          set(
            state => {
              const next = new Set(state.removingIds);
              next.delete(id);
              return { removingIds: next, error: extractError(err) };
            },
            false,
            'competitors/remove/error'
          );
        }
      },

      fetchPosts: async params => {
        set({ isLoadingPosts: true, error: null }, false, 'competitors/fetchPosts');
        try {
          const result = await competitorApi.getPosts(params);
          set(
            {
              competitorPosts: result.data ?? [],
              page: result.page ?? 1,
              totalPages: result.totalPages ?? 1,
              total: result.total ?? 0,
              isLoadingPosts: false,
            },
            false,
            'competitors/fetchPosts/success'
          );
        } catch (err) {
          set(
            { isLoadingPosts: false, error: extractError(err) },
            false,
            'competitors/fetchPosts/error'
          );
        }
      },

      fetchInsights: async () => {
        set({ isLoadingInsights: true, error: null }, false, 'competitors/fetchInsights');
        try {
          const insights = await competitorApi.getInsights();
          set({ insights, isLoadingInsights: false }, false, 'competitors/fetchInsights/success');
          return true;
        } catch (err) {
          set(
            { isLoadingInsights: false, error: extractError(err) },
            false,
            'competitors/fetchInsights/error'
          );
          return false;
        }
      },

      clearError: () => set({ error: null }, false, 'competitors/clearError'),
    }),
    {
      name: 'CompetitorStore',
      enabled: import.meta.env.DEV,
    }
  )
);
